'use strict';

const React = require('react');
const PropTypes = require('prop-types');
const animationFrame = require('../utils/requestAnimationFrame');
const classnames = require('classnames');
const browserCheck = require('bunsen/helpers/browserCheck');

const DEFAULT_WIDTH = '100%';
const DEFAULT_LEFT = '0px';

class Sticky extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            top: 0,
            fixed: false,
            width: DEFAULT_WIDTH,
            left: DEFAULT_LEFT
        };

        this._setWidth = this._setWidth.bind(this);
        this._setPosition = this._setPosition.bind(this);
        this._trackPosition = this._trackPosition.bind(this);
    }

    componentDidMount() {
        const sticky = this._sticky;

        if (window.getComputedStyle(sticky).position === 'relative') {
            // position: sticky is not widely supported yet so the side bar is
            // using position: relative until it is - using "this.legacy" to denote this.
            // change position: relative to position: sticky and refactor when support is up.
            this.legacy = true;

            animationFrame(() => {
                this._setPosition();
                this._setWidth();
            });

            window.addEventListener('scroll', this._setPosition);
            window.addEventListener('resize', this._setWidth);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (this.legacy) {
            // Use `resetPosition` like a trigger to set off position tracking.
            if (nextProps.resetPosition && !this.props.resetPosition) {
                this._trackPosition();
            }
        }
    }

    componentWillUnmount() {
        if (this.legacy) {
            window.removeEventListener('scroll', this._setPosition);
            window.removeEventListener('resize', this._setWidth);
        }
    }

    // If the `trackPosition` props gets set to true, keep adjusting the position until it is set to false. This allows
    // the sticky component to adjust itself in response to other components being animated.
    _trackPosition() {
        animationFrame(() => {
            this._setPosition();

            if (this.props.resetPosition) {
                this._trackPosition();
            }
        });
    }

    _setWidth() {
        const {calculateWidth, calculateLeft} = this.props;

        this.setState({
            width: calculateWidth ? calculateWidth() : DEFAULT_WIDTH,
            left: calculateLeft ? calculateLeft() : DEFAULT_LEFT
        });
    }

    _setPosition() {
        if (this.isAnimating) {
            return;
        }

        this.isAnimating = true;

        animationFrame(() => {
            const offsetTop = this.props.offsetTop;
            const sticky = this._sticky;
            const child = sticky.firstChild;
            const parent = sticky.parentElement;
            const parentBoundingRect = parent.getBoundingClientRect();
            const stickyHeight = child.clientHeight;
            const parentTop = parentBoundingRect.top - offsetTop;
            const parentBottom = parentBoundingRect.bottom - offsetTop;

            let top = 0;
            let fixed = false;

            // Prevents the sticky component from moving past the bottom of its parent.
            if (stickyHeight >= parentBottom) {
                top -= stickyHeight + parentTop - parentBottom;

            // This can be below 0 because the bounding box top is measured from the top of the viewport, not the top
            // of the document.
            } else if (parentTop < 0) {
                fixed = true;
                top = offsetTop;

            // If the sticky component is both above the bottom of its parent and the parent is below the top of the
            // viewport (page scrolled to its top), then reset the position flag and top to its parents' top.
            } else if (parentTop >= 0) {
                top = 0;
            }

            // Always reset the position
            this.setState({ fixed, top });
            this.isAnimating = false;
        });
    }

    render() {
        const {className, offsetTop, children} = this.props;
        const {top, width, left, fixed} = this.state;

        const classConfig = classnames('sticky-wrapper', {
            [className]: className
        });

        let style;

        if (this.legacy) {
            style = { top, width };

            // IE 11 calculates this width differently than other browsers.
            // Need to subtract 2 to correct the value.
            if (browserCheck.isIE11() && style.width) {
                style.width -= 2;
            }

            if (fixed) {
                style.position = 'fixed';
                style.left = left;
            }
        } else {
            style = {
                top: offsetTop
            };
        }

        return (
            <div ref={(sticky => { this._sticky = sticky; })} style={style} className={classConfig}>
                {children}
            </div>
        );
    }
}

Sticky.defaultProps = {
    offsetTop: 0,
    className: ''
};

Sticky.propTypes = {
    children: PropTypes.element,
    offsetTop: PropTypes.number,
    resetPosition: PropTypes.bool,
    className: PropTypes.string,
    calculateWidth: PropTypes.func,
    calculateLeft: PropTypes.func
};

module.exports = Sticky;



// WEBPACK FOOTER //
// ./assets/js/global/components/Sticky.jsx